<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import EventPicker from 'dashboard/components-next/planning/EventPicker.vue';
import {
  PORTAL_PREVIEW_STUB_ID,
  isPortalPreviewStub,
} from '../lib/previewEventStub';

const props = defineProps({
  selectedEventId: { type: [String, Number], default: null },
  selectedEventName: { type: String, default: '' },
});

const emit = defineEmits(['update:selectedEventId']);
const { t } = useI18n();

const open = ref(false);

const showingStub = computed(() => isPortalPreviewStub(props.selectedEventId));

const label = computed(() => {
  if (showingStub.value) {
    return t('PLANNING.EVENT_PORTALS.EDITOR.SAMPLE_EVENT.BADGE');
  }
  return (
    props.selectedEventName ||
    t('PLANNING.EVENT_PORTALS.EDITOR.SAMPLE_EVENT.BADGE')
  );
});

const pickerModel = computed({
  get: () => (showingStub.value ? null : props.selectedEventId),
  set: value => {
    emit('update:selectedEventId', value || PORTAL_PREVIEW_STUB_ID);
    open.value = false;
  },
});

const selectStub = () => {
  emit('update:selectedEventId', PORTAL_PREVIEW_STUB_ID);
  open.value = false;
};

const toggle = () => {
  open.value = !open.value;
};

const close = () => {
  open.value = false;
};

const onKeyDown = e => {
  if (e.key === 'Escape') close();
};

onMounted(() => document.addEventListener('keydown', onKeyDown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeyDown));
</script>

<template>
  <OnClickOutside @trigger="close">
    <div class="relative">
      <button
        type="button"
        class="flex items-center gap-1.5 max-w-[220px] h-8 px-2 rounded-md text-xs font-medium transition-colors"
        :class="
          open
            ? 'bg-n-slate-3 text-n-slate-12'
            : 'text-n-slate-11 hover:bg-n-slate-3 hover:text-n-slate-12'
        "
        :title="t('PLANNING.EVENT_PORTALS.EDITOR.SAMPLE_EVENT.PICKER_TITLE')"
        @click="toggle"
      >
        <span class="i-lucide-calendar-days size-3.5 shrink-0" />
        <span class="truncate">{{ label }}</span>
        <span
          class="i-lucide-chevron-down size-3 shrink-0 transition-transform"
          :class="open ? 'rotate-180' : ''"
        />
      </button>

      <div
        v-if="open"
        class="absolute right-0 top-full mt-1.5 z-50 w-72 rounded-xl border border-n-slate-5 bg-white p-3 shadow-lg"
      >
        <button
          type="button"
          class="flex items-center justify-between gap-2 w-full px-2.5 py-2 rounded-lg text-left transition-colors mb-2"
          :class="
            showingStub
              ? 'bg-n-brand-3 text-n-brand-11'
              : 'bg-n-slate-2 text-n-slate-11 hover:bg-n-slate-3'
          "
          @click="selectStub"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span class="i-lucide-sparkles size-3.5 shrink-0" />
            <span class="text-xs font-medium truncate">
              {{ t('PLANNING.EVENT_PORTALS.EDITOR.SAMPLE_EVENT.BADGE') }}
            </span>
          </div>
          <span
            v-if="showingStub"
            class="shrink-0 text-[10px] font-semibold uppercase tracking-wide"
          >
            {{ t('PLANNING.EVENT_PORTALS.EDITOR.SAMPLE_EVENT.ACTIVE') }}
          </span>
        </button>

        <p
          class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-9 mb-1.5 px-0.5"
        >
          {{ t('PLANNING.EVENT_PORTALS.EDITOR.SAMPLE_EVENT.OR_REAL') }}
        </p>
        <EventPicker v-model="pickerModel" />
      </div>
    </div>
  </OnClickOutside>
</template>
